import { is, isEmpty, sort, prop, find, propEq, ascend, descend, includes } from 'ramda';

/**
 * Helper function to verify that passed argument is a valid
 * array with content. Used both as a truthty value and as a plucker
 * @function isArrayWithContent
 * @param {Array} array
 * @returns {Boolean}
 */
export const isArrayWithContent = array => {
	if (is(Array, array) && !isEmpty(array)) {
		return true;
	}
	return false;
};

/**
 * Helper function to verify that passed argument is a valid empty array
 * @function isArrayWithoutContent
 * @param {Array} array
 * @returns {Boolean}
 */
export const isArrayWithoutContent = array => {
	if (is(Array, array) && isEmpty(array)) {
		return true;
	}
	return false;
};

/**
 * Helper function used to toggle value in array
 * Usecase: if value exists in array return array without it, otherwise add it and return new array
 * @function toggleArrayContent
 * @param {Array} array
 * @param {any} value
 * @returns {Array}
 */
export const toggleArrayContent = (array, value) => {
	if (Array.isArray(array) === false) {
		return [];
	}

	if (includes(value, array)) {
		return array.filter(v => v !== value);
	}

	return array.concat(value);
};

/**
 * Helper function used to toggle object by specific key in array
 * Usecase: if value exists in array return array without it, otherwise add it and return new array
 * @function toggleArrayContent
 * @param {Array} array
 * @param {Object} object
 * @param {String} key
 * @returns {Array}
 */
export const toggleArrayContentByKey = (array, object, key = 'id') => {
	if (Array.isArray(array) === false) {
		return [];
	}

	if (find(propEq(key, object[key]))(array)) {
		return array.filter(v => v[key] !== object[key]);
	}

	return array.concat(object);
};

/**
 * @function sortObjectsInArrayByKey
 * @param {Array} array
 * @param {String} objectKey
 * @param {Boolean} ascending
 * @returns {Array} with objects sorted
 */
export const sortObjectsInArrayByKey = (array, objectKey, ascending) => {
	if (isArrayWithContent(array) === false) {
		return [];
	}

	if (ascending) {
		return sort(ascend(prop(objectKey)), array);
	}

	return sort(descend(prop(objectKey)), array);
};

/**
 * @function mergeObjectsInUnique
 * @description https://medium.com/swlh/javascript-merge-duplicate-objects-in-array-of-objects-9a76c3a1c35c
 * @param {Array} array
 * @returns {Array} with objects sorted
 */
export function mergeObjectsInUnique(array, property) {
	const newArray = new Map();
	array.forEach(item => {
		const propertyValue = item[property];
		newArray.has(propertyValue)
			? newArray.set(propertyValue, { ...item, ...newArray.get(propertyValue) })
			: newArray.set(propertyValue, item);
	});

	return Array.from(newArray.values());
}

export default isArrayWithContent;
